import { Partner } from 'src/app/core/schema';
import { licenseIsActive } from 'src/app/domain/licenses/services/license-utils';
import { PERMISSION_ROLES, type PermissionRoleId } from 'src/app/domain/permissions/types/Permissions.model';

/** The role a domain-based join grants (DOMAIN_JOIN_ROLE in functions/src/lib/domain-join.ts). */
export const DOMAIN_JOIN_ROLE_ID: PermissionRoleId = 'Educator';

/** How that role is shown to people ("User"). */
export const DOMAIN_JOIN_ROLE_NAME =
  PERMISSION_ROLES.find(role => role.id === DOMAIN_JOIN_ROLE_ID)?.name ?? DOMAIN_JOIN_ROLE_ID;

/** What domain-based invitations do for one team right now. */
export interface DomainInvitationStatus {
  /** The setting itself: absent reads as on. */
  readonly on: boolean;
  /** One sentence for the read-only Settings card. */
  readonly summary: string;
}

/**
 * The state of a team's domain-based invitations, mirroring the server rules
 * in functions/src/lib/domain-join.ts: on unless switched off, nothing offered
 * without domains, and paused without a live licence (any module, trial or paid).
 */
export function domainInvitationStatus(
  partner: Pick<Partner, 'domainAuthEnabled' | 'domains' | 'licenses'> | null | undefined,
  now: number = Date.now(),
): DomainInvitationStatus {
  const on = partner?.domainAuthEnabled !== false;
  if (!on) {
    return { on, summary: 'Off. No one is offered this team based on their email address.' };
  }
  if (!partner?.domains?.length) {
    return { on, summary: 'On, but no domains are set, so no one is offered this team.' };
  }
  if (!(partner?.licenses ?? []).some(license => licenseIsActive(license, now))) {
    return {
      on,
      summary: 'Paused: this team has no active license, so no one is offered it until one starts.',
    };
  }
  return {
    on,
    summary: `People who verify an email address at these domains are offered this team, and join as a ${DOMAIN_JOIN_ROLE_NAME}. Anyone removed from the team, or whose invitation was revoked, is not offered it.`,
  };
}
